import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { PandaDocAnalyticsData } from 'core/hooks';

import * as S from '../PandaDocAnalytics.styled';
import { COLORS } from '../constants';
import { KpiCard, ChartCard } from '../components';
import { ConversionNote } from '../ConversionNote';
import { HistogramChart } from '../charts';
import { fmtNum, fmtPct, fmtTTS } from '../utils';

interface IOverviewTabProps {
  data: PandaDocAnalyticsData;
}

/** High-level tab: the three KPI groups and the TTS distribution. */
export const OverviewTab = memo(({ data }: IOverviewTabProps) => {
  const { t } = useTranslation();
  const volume = data.volumeKpi;
  const tts = data.ttsKpi;
  const conversion = data.conversionKpi;

  return (
    <>
      {/* ---- volume ---- */}
      <S.SectionTitle>
        {t('pages.pandadocAnalytics.overview.volumeTitle')}
      </S.SectionTitle>
      <S.FooterNote style={{ marginTop: 0, marginBottom: 8 }}>
        {t('pages.pandadocAnalytics.overview.volumeFooter')}
      </S.FooterNote>
      <S.KpiGrid columns={5}>
        <KpiCard
          label={t('pages.pandadocAnalytics.overview.kpiTotal')}
          value={fmtNum(volume?.total)}
          subLabel={t('pages.pandadocAnalytics.overview.kpiTotalSub')}
        />
        <KpiCard
          label={t('pages.pandadocAnalytics.overview.kpiStudents')}
          value={fmtNum(volume?.students)}
          subLabel={t('pages.pandadocAnalytics.overview.kpiStudentsSub')}
          accent={COLORS.students}
        />
        <KpiCard
          label={t('pages.pandadocAnalytics.overview.kpiTrainers')}
          value={fmtNum(volume?.trainers)}
          subLabel={t('pages.pandadocAnalytics.overview.kpiTrainersSub')}
          accent={COLORS.trainers}
        />
        <KpiCard
          label={t('pages.pandadocAnalytics.overview.kpiDofin')}
          value={fmtNum(volume?.dofin)}
          subLabel={t('pages.pandadocAnalytics.overview.kpiDofinSub')}
          accent={COLORS.dofin}
        />
        <KpiCard
          label={t('pages.pandadocAnalytics.overview.kpiLast30')}
          value={fmtNum(volume?.last30)}
          subLabel={t('pages.pandadocAnalytics.overview.kpiLast30Sub')}
        />
      </S.KpiGrid>

      {/* ---- time-to-sign ---- */}
      <S.SectionTitle>
        {t('pages.pandadocAnalytics.overview.ttsTitle')}
      </S.SectionTitle>
      <S.FooterNote style={{ marginTop: 0, marginBottom: 8 }}>
        {t('pages.pandadocAnalytics.overview.ttsFooter', {
          sample: fmtNum(tts?.sample),
        })}
      </S.FooterNote>
      <S.KpiGrid columns={4}>
        <KpiCard
          label={t('pages.pandadocAnalytics.overview.kpiMedianTts')}
          value={fmtTTS(tts?.medianHours, t)}
          subLabel={t('pages.pandadocAnalytics.overview.kpiMedianTtsSub')}
          accent={COLORS.tts}
        />
        <KpiCard
          label={t('pages.pandadocAnalytics.overview.kpiMeanTts')}
          value={fmtTTS(tts?.meanHours, t)}
          subLabel={t('pages.pandadocAnalytics.overview.kpiMeanTtsSub')}
          accent={COLORS.tts}
        />
        <KpiCard
          label={t('pages.pandadocAnalytics.overview.kpiPct24h')}
          value={fmtPct(tts?.pct24h)}
          subLabel={t('pages.pandadocAnalytics.overview.kpiPct24hSub')}
          accent={COLORS.tts}
        />
        <KpiCard
          label={t('pages.pandadocAnalytics.overview.kpiPct3d')}
          value={fmtPct(tts?.pct3d)}
          subLabel={t('pages.pandadocAnalytics.overview.kpiPct3dSub')}
          accent={COLORS.tts}
        />
      </S.KpiGrid>

      {/* ---- conversion ---- */}
      <S.SectionTitle>
        {t('pages.pandadocAnalytics.overview.conversionTitle')}
      </S.SectionTitle>
      <ConversionNote />
      <S.KpiGrid columns={5}>
        <KpiCard
          label={t('pages.pandadocAnalytics.overview.kpiSent')}
          value={fmtNum(conversion?.sent)}
          subLabel={t('pages.pandadocAnalytics.overview.kpiSentSub')}
        />
        <KpiCard
          label={t('pages.pandadocAnalytics.overview.kpiSigned')}
          value={fmtNum(conversion?.signed)}
          subLabel={fmtPct(conversion?.signedPct)}
          accent={COLORS.signed}
        />
        <KpiCard
          label={t('pages.pandadocAnalytics.overview.kpiActive')}
          value={fmtNum(conversion?.active)}
          subLabel={fmtPct(conversion?.activePct)}
          accent={COLORS.active}
        />
        <KpiCard
          label={t('pages.pandadocAnalytics.overview.kpiLost')}
          value={fmtNum(conversion?.lost)}
          subLabel={fmtPct(conversion?.lostPct)}
          accent={COLORS.lost}
        />
        <KpiCard
          label={t('pages.pandadocAnalytics.overview.kpiConversion')}
          value={fmtPct(conversion?.ratePct)}
          subLabel={t('pages.pandadocAnalytics.overview.kpiConversionSub')}
          accent={COLORS.signed}
        />
      </S.KpiGrid>

      <S.SectionTitle>
        {t('pages.pandadocAnalytics.overview.distributionTitle')}
      </S.SectionTitle>
      <ChartCard
        title={t('pages.pandadocAnalytics.overview.ttsHistogramTitle')}
        meta={t('pages.pandadocAnalytics.overview.sampleMeta', {
          sample: fmtNum(tts?.sample),
        })}
      >
        <HistogramChart
          labels={data.ttsHistogram?.labels ?? []}
          values={data.ttsHistogram?.values ?? []}
        />
      </ChartCard>
    </>
  );
});
